/**
 * Historical academic-record import API (issue #96) — batches, receipts, and
 * the append-only correction chain.
 *
 * Typed wrappers over the shared `api` client, mirroring academicStructure.ts's
 * shape. Every route here is admin-only server-side; this client makes no
 * authority claim.
 *
 * The two create counts are deliberately separate rather than one number the
 * caller reinterprets by mode. `created_count` is what a run actually wrote and
 * migration 034 makes it unrepresentable for a dry run; `projected_create_count`
 * is what an apply of the same source WOULD write. A surface that renders one
 * where the other belongs states something false about the record.
 *
 * `run` posts BOTH modes to the one route and both answer 201, because a dry run
 * is a run: it opens a real batch and writes real receipts, and the only thing it
 * does not do is write a product row. So the result of either is an
 * `ImportBatchDetail`, and the mode on the batch it returns, not the mode that
 * was asked for, is what says which counts it is entitled to report.
 *
 * `source_digest` is the digest of the FILE and the caller computes it, because
 * the server never sees the file; it sees the payload parsed from it. That is
 * what lets an operator asking "was this export already run?" be answered about
 * the export rather than about whatever it happened to parse to, so a caller
 * that hashes a re-serialised object here answers a different question in the
 * same field.
 */
import { api } from "@/lib/api/client";
import { checkCommit, commitOptions, decodeBulkResult, retryOptions, type BulkResult } from "./commitSeams";
import { withVersion } from "./conflict";

export type ImportMode = "dry_run" | "apply";
/**
 * Which source contract a run read (ADR 0036). `historical` is the closed-term
 * source of ADR 0012; `registrar_roster` is a live-term roster of students and
 * enrollments, reconciled by exact SID and never by email. The server records
 * it on the batch and a dry run clears an apply of the same kind only.
 */
export type ImportKind = "historical" | "registrar_roster";
export type ImportStatus = "running" | "completed" | "failed";
export type EntityKind = "term" | "course" | "faculty" | "student" | "enrollment" | "outcome";
export type ReceiptAction =
  | "created"
  | "matched"
  | "skipped"
  | "rejected"
  | "corrected"
  | "would_create";

export interface ImportBatch {
  id: string;
  source_name: string;
  source_digest: string;
  mode: ImportMode;
  /** Absent only while an older backend is serving during a paired release. */
  kind?: ImportKind;
  status: ImportStatus;
  created_count: number;
  projected_create_count: number;
  matched_count: number;
  skipped_count: number;
  rejected_count: number;
  outcome: string | null;
  actor_user_id: string;
  /** A server-resolved actor name or role, never inferred from the UUID. */
  actor_display_name?: string;
  started_at: string;
  finished_at: string | null;
}

export interface ImportReceipt {
  id: string;
  batch_id: string;
  import_key: string;
  entity_kind: EntityKind;
  /** Opaque source payload, recorded per row. Rendered verbatim, never parsed. */
  source_ref: Record<string, unknown>;
  resolved_id: string | null;
  /** Server-verified applied, same-tenant institutional destination. */
  resolved_record?: { kind: "course" | "student"; id: string; person_id?: string } | null;
  action: ReceiptAction;
  reason: string | null;
  corrects_receipt_id: string | null;
  prior_value: Record<string, unknown> | null;
  created_at: string;
}

/**
 * The service-owned keys a receipt's `source_ref` carries beside the source's
 * own evidence. `_imam_row_data` is the row's `data` exactly as it was sent,
 * valid or not, so a refused row can be rebuilt, fixed and sent again; runs
 * recorded before it existed lack it. These keys are never source evidence.
 */
export const RECEIPT_SERVICE_KEYS = ["_imam_row_sha256", "_imam_learner_choice", "_imam_row_data"] as const;

/** The row a receipt recorded, rebuilt for another run; null when the run predates row data. */
export function receiptRow(receipt: ImportReceipt): ImportRowInput | null {
  const ref = receipt.source_ref ?? {};
  const data = ref._imam_row_data;
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const evidence = Object.fromEntries(Object.entries(ref).filter(([key]) => !(RECEIPT_SERVICE_KEYS as readonly string[]).includes(key)));
  return { entity_kind: receipt.entity_kind, import_key: receipt.import_key, source_ref: evidence, data: data as Record<string, unknown> };
}

/** The registrar's recorded learner choice on a receipt, when one was made. */
export function receiptLearnerChoice(receipt: ImportReceipt): LearnerChoice | null {
  const choice = receipt.source_ref?._imam_learner_choice as { action?: string; student_id?: string | null } | undefined;
  if (!choice?.action) return null;
  return choice.action === "link" && choice.student_id ? { action: "link", student_id: choice.student_id }
    : choice.action === "create" || choice.action === "leave_out" ? { action: choice.action } : null;
}

/** A page of the run register. Newest first; `before` names the last run already read. */
export interface ImportBatchQuery {
  mode?: ImportMode;
  kind?: ImportKind;
  /** Case-insensitive substring of the source name. */
  q?: string;
  /** A batch id: returns only runs older than it, by (started_at, id). */
  before?: string;
  /** 1 to 500; the server's default is 100. */
  limit?: number;
}

/** Register-wide totals, independent of any filter or page. */
export interface ImportSummary {
  applied_runs: number;
  dry_runs: number;
  /** Sum of created_count over applied runs. */
  records_written: number;
  /** Sum of rejected_count over applied runs. */
  refused_rows: number;
  last_applied_at: string | null;
}

/** One term's recorded academic history: what exists and what is still incomplete. */
export interface TermCoverage {
  term_id: string;
  name: string;
  start_date: string | null;
  end_date: string | null;
  /** lms_semester.status as stored. */
  status: string;
  sections: number;
  /** Sections whose identity came from an import. */
  imported_sections: number;
  sections_without_instructor: number;
  sections_without_enrollment: number;
  enrollments: number;
  certified_outcomes: number;
  /** Enrollments in this term with no standing certified outcome. */
  enrollments_without_outcome: number;
  /** The newest applied run that wrote or matched a record in this term. */
  last_run_id: string | null;
  last_run_at: string | null;
}

export interface ImportCoverage { terms: TermCoverage[] }

export interface ImportObservation {
  kind: string;
  detail: string;
}

export interface ImportBatchDetail {
  batch: ImportBatch;
  receipts: ImportReceipt[];
  observations: ImportObservation[];
  omitted: string[];
}

export type ImportRunResult = ImportBatchDetail;

/**
 * One source row, keyed by the operator, carried as written.
 *
 * `data` and `source_ref` stay open mappings on purpose. The server validates
 * `data` per `entity_kind` INSIDE the run rather than in the envelope, precisely
 * so one malformed row becomes one rejected receipt instead of a 422 for the
 * whole file, and a discriminated union here would refuse the payload before
 * the dry run could report it row by row, which is the one thing a dry run is
 * for. The shapes are also not settled: the mapping from a registrar export to
 * this payload waits on an authoritative registrar sample, so a client copy of the
 * per-kind rules would be a second set of rules drifting from the first.
 *
 * `entity_kind` is typed exactly, because it is closed on the server and closed
 * for a stated reason: attendance is not a member, and a payload naming it is
 * refused rather than silently dropped.
 */
export interface ImportRowInput {
  entity_kind: EntityKind;
  import_key: string;
  source_ref?: Record<string, unknown>;
  data?: Record<string, unknown>;
}

export interface ImportRunRequest {
  source_name: string;
  /** Lowercase hex SHA-256 of the source bytes. Of the file, not of these rows. */
  source_digest: string;
  mode: ImportMode;
  /** Defaults to `historical` on the server. */
  kind?: ImportKind;
  rows: ImportRowInput[];
  learner_choices?: Record<string, LearnerChoice>;
  retry_ids?: string[];
}

export type LearnerChoice = { action: "link"; student_id: string } | { action: "create" | "leave_out" };
export interface ImportApplyResult extends BulkResult { batch_ids: string[] }
export function decodeImportApply(value: unknown): ImportApplyResult {
  const bulk = decodeBulkResult(value);
  const refs = (value as Record<string, unknown>).batch_ids;
  if (!Array.isArray(refs) || !refs.length || refs.some((id) => typeof id !== "string" || !id)) throw new TypeError("Expected recorded import runs");
  return { ...bulk, batch_ids: refs };
}

/**
 * Whether a student an applied registrar roster names can be invited now, in
 * the order the invitation guards refuse (ADR 0036). `invitable` is the only
 * state the batch invitation issues for; every other state names its reason.
 */
export type RosterInvitationState =
  | "invitable"
  | "signs_in"
  | "invitation_open"
  | "inactive"
  | "no_contact"
  | "not_roster_recorded";

export interface RosterStudent {
  student_id: string;
  sid: string;
  full_name: string | null;
  /** The recorded delivery contact. A contact, never an identity. */
  email: string | null;
  import_key: string;
  open_invitation_email: string | null;
  state: RosterInvitationState;
}

export interface InviteStudentsRequest { role: "student"; batch_id: string | null; invitations: Array<{ student_id: string; managed_account: string; delivery_email: string; given_name: string; family_name: string }> }

export interface CorrectionRequest {
  corrects_receipt_id: string;
  reason: string;
  evidence_ref: string;
  downstream_impact: string;
  data: Record<string, unknown>;
}
export interface CorrectionCommitResult { state: "confirmed"; batch_ids: string[]; receipt_ids: string[]; version: string }

export const importsApi = {
  inviteStudents: async (body: InviteStudentsRequest, key: string, retry = false) => decodeBulkResult(await api.post<unknown>("/invitations/bulk", body, retry ? retryOptions(key) : commitOptions(key))),
  checkInvitations: (key: string) => checkCommit(key, decodeBulkResult),
  ranking: () => api.get<{ state: "ready"; value: { candidates: Array<{ id: string; rank: number | null }> } } | { state: "empty" | "unavailable" }>("/imports/ranking"),
  getBatches: () => api.get<ImportBatch[]>("/imports"),
  /** Takes an explicit query; never pass it to react-query as a bare queryFn, which would send its context. */
  listBatches: (query: ImportBatchQuery) => {
    const params = new URLSearchParams(Object.entries(query).filter(([, v]) => v !== undefined && v !== "").map(([k, v]) => [k, String(v)]));
    return api.get<ImportBatch[]>(params.size ? `/imports?${params}` : "/imports");
  },
  getSummary: () => api.get<ImportSummary>("/imports/summary"),
  getCoverage: () => api.get<ImportCoverage>("/imports/coverage"),
  getBatch: (batchId: string) => api.get<ImportBatchDetail>(`/imports/${batchId}`),

  run: (body: ImportRunRequest) => api.post<ImportRunResult>("/imports", body),
  apply: async (body: ImportRunRequest, key: string, retryIds?: string[]) => decodeImportApply(
    await api.post<unknown>("/imports", { ...body, mode: "apply", ...(retryIds ? { retry_ids: retryIds } : {}) }, retryIds ? retryOptions(key) : commitOptions(key))),
  checkApply: (key: string) => checkCommit(key, decodeImportApply),
  correct: (body: CorrectionRequest, version: string, key: string) => api.post<CorrectionCommitResult>("/imports/corrections", withVersion(body, version), commitOptions(key)),
  getReceiptHistory: (receiptId: string) =>
    api.get<ImportReceipt[]>(`/imports/receipts/${receiptId}/history`),
  getRosterStudents: (batchId: string) =>
    api.get<RosterStudent[]>(`/imports/${batchId}/roster-students`),
};
